/**
 * DroneStatus.jsx
 * ===============
 * Floating panel (top-right of map) showing live drone telemetry:
 *  - Battery %, GPS coordinates, altitude, satellite count
 *  - Capture mode (Online / Offline) and capture status
 *  - MQTT connection indicator
 *  - Image counter
 *
 * Receives real-time updates via Socket.IO events:
 *  - "drone_position"   → {lat, lon, alt, sats, valid, timestamp}
 *  - "drone_state_sync" → {battery, mode, capturing, capture_count, …}
 *  - "new_classification" → triggers image count update
 *
 * Also polls /api/drone/status every 10 s as fallback.
 */

import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Try to import socket.io-client — gracefully degrade if not installed
let io = null;
try {
  io = require('socket.io-client');
} catch {
  // socket.io-client not installed — will poll HTTP instead
}

// Battery icon thresholds
function BatteryIcon({ pct }) {
  const color = pct > 50 ? '#4ade80' : pct > 20 ? '#fbbf24' : '#f87171';
  const fill  = Math.max(0, Math.min(100, pct));
  return (
    <svg width="24" height="12" viewBox="0 0 24 12">
      <rect x="0" y="1" width="20" height="10" rx="2" ry="2"
        stroke={color} strokeWidth="1.5" fill="none" />
      <rect x="20" y="4" width="3" height="4" rx="1" fill={color} />
      <rect x="1.5" y="2.5" width={Math.round(17 * fill / 100)} height="7"
        rx="1" fill={color} />
    </svg>
  );
}

// Signal bars icon
function SignalIcon({ sats }) {
  const bars  = Math.min(4, Math.floor(sats / 2));
  const color = sats >= 4 ? '#4ade80' : sats >= 2 ? '#fbbf24' : '#f87171';
  return (
    <svg width="16" height="12" viewBox="0 0 16 12">
      {[0, 1, 2, 3].map(i => (
        <rect key={i}
          x={i * 4 + 0.5} y={12 - (i + 1) * 3}
          width={3} height={(i + 1) * 3}
          rx="0.5"
          fill={i < bars ? color : '#334155'}
        />
      ))}
    </svg>
  );
}

export default function DroneStatus({ onPositionUpdate, simGps, isCapturing }) {
  const [status,   setStatus]   = useState(null);
  const [gps,      setGps]      = useState(null);
  const [mqttOk,   setMqttOk]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [collapsed, setCollapsed]  = useState(() => window.innerWidth < 768);
  const [captureCount, setCaptureCount] = useState(0);
  const socketRef  = useRef(null);
  const pollRef    = useRef(null);

  // ── Track capture count from simulation ──────────────────────────────────
  useEffect(() => {
    if (!isCapturing) { setCaptureCount(0); return; }
    if (simGps) setCaptureCount(prev => prev + 1);
  }, [simGps]);

  useEffect(() => {
    if (!isCapturing) setCaptureCount(0);
  }, [isCapturing]);

  // ── HTTP polling fallback ─────────────────────────────────────────────────
  const pollStatus = async () => {
    try {
      const [sResp, pResp] = await Promise.all([
        fetch(`${API_BASE}/api/drone/status`),
        fetch(`${API_BASE}/api/drone/position`),
      ]);
      if (sResp.ok) {
        const s = await sResp.json();
        setStatus(s);
        setMqttOk(s.mqtt_connected || false);
      }
      if (pResp.ok) {
        const p = await pResp.json();
        setGps(p);
        onPositionUpdate && onPositionUpdate(p);
      }
      setLastUpdate(new Date());
    } catch {
      // Backend offline — keep last state
    }
  };

  // ── Socket.IO connection ──────────────────────────────────────────────────
  useEffect(() => {
    // Start HTTP poll immediately (works even without socket.io-client)
    pollStatus();
    pollRef.current = setInterval(pollStatus, 10_000);

    if (!io) return () => clearInterval(pollRef.current);

    try {
      const socket = io(API_BASE, { transports: ['websocket', 'polling'] });
      socketRef.current = socket;

      socket.on('connect', () => setMqttOk(true));
      socket.on('disconnect', () => setMqttOk(false));

      socket.on('drone_position', (data) => {
        setGps(data);
        onPositionUpdate && onPositionUpdate(data);
        setLastUpdate(new Date());
      });

      socket.on('drone_state_sync', (data) => {
        setStatus(data);
        setLastUpdate(new Date());
      });

      socket.on('new_classification', (data) => {
        // Update capture count
        setStatus(prev => prev
          ? { ...prev, capture_count: (prev.capture_count || 0) + 1 }
          : prev
        );
        setLastUpdate(new Date());
      });
    } catch (e) {
      // Socket.IO connect failed — poll-only mode
    }

    return () => {
      clearInterval(pollRef.current);
      socketRef.current?.disconnect();
    };
  }, []);

  // ── Render — simulation overrides real backend data when capturing ────────
  const activeGps = isCapturing && simGps ? simGps : gps;
  const bat       = isCapturing ? 85           : (status?.battery ?? null);
  const mode      = isCapturing ? 'online'     : (status?.mode ?? '—');
  const count     = isCapturing ? captureCount : (status?.capture_count ?? 0);
  const sats      = isCapturing ? 10           : (activeGps?.sats ?? status?.satellites ?? 0);
  const lat       = activeGps?.lat;
  const lon       = activeGps?.lon;
  const alt       = activeGps?.alt;
  const gpsOk     = isCapturing ? true         : (activeGps?.valid ?? false);
  const capturing = isCapturing;
  const liveOk    = isCapturing || mqttOk;

  const timeSince = lastUpdate
    ? Math.round((Date.now() - lastUpdate) / 1000)
    : null;

  return (
    <div
      className="absolute top-2 right-2 md:top-3 md:right-14 z-[1001] select-none"
      style={{ minWidth: collapsed ? 'auto' : 200 }}
    >
      {/* Header bar */}
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-t-lg cursor-pointer"
        style={{
          background: 'rgba(10,20,35,0.95)',
          borderBottom: '1px solid rgba(45,74,110,0.4)',
          border: '1px solid rgba(45,74,110,0.6)',
          borderBottomColor: collapsed ? undefined : 'transparent',
          borderRadius: collapsed ? 8 : '8px 8px 0 0',
        }}
        onClick={() => setCollapsed(c => !c)}
      >
        {/* Drone icon + pulsing indicator */}
        <span className="text-base">🚁</span>
        <span className="text-xs font-bold text-white">drone001</span>
        <div className="flex-1" />
        {/* MQTT / sim status dot */}
        <div className="flex items-center gap-1">
          <div
            className={`w-2 h-2 rounded-full ${liveOk ? 'pulse-dot' : ''}`}
            style={{ background: liveOk ? '#4ade80' : '#f87171' }}
            title={liveOk ? 'Drone Active' : 'Drone Offline'}
          />
          <span className="text-xs font-bold" style={{ color: liveOk ? '#4ade80' : '#f87171' }}>
            {liveOk ? 'ONLINE' : 'OFFLINE'}
          </span>
        </div>
        <span className="text-slate-500 text-xs ml-1">{collapsed ? '▼' : '▲'}</span>
      </div>

      {/* Body */}
      {!collapsed && (
        <div
          className="px-3 py-2 space-y-2 rounded-b-lg fade-in"
          style={{
            background: 'rgba(10,20,35,0.95)',
            border: '1px solid rgba(45,74,110,0.6)',
            borderTop: 'none',
          }}
        >
          {/* Battery + Capture mode */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              {bat !== null && <BatteryIcon pct={bat} />}
              <span
                className="text-xs font-bold"
                style={{
                  color: bat > 50 ? '#4ade80' : bat > 20 ? '#fbbf24' : '#f87171'
                }}
              >
                {bat !== null ? `${bat}%` : '—'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: capturing ? '#4ade80' : '#64748b' }}
              />
              <span
                className="text-xs font-semibold px-1.5 py-0.5 rounded"
                style={{
                  background: mode === 'online' ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.2)',
                  color: mode === 'online' ? '#4ade80' : '#94a3b8',
                }}
              >
                {mode.toUpperCase()}
              </span>
            </div>
          </div>

          {/* GPS coordinates */}
          <div
            className="rounded p-2 space-y-1"
            style={{ background: 'rgba(255,255,255,0.04)' }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">GPS</span>
              <div className="flex items-center gap-1">
                <SignalIcon sats={sats} />
                <span className="text-xs text-slate-400">{sats} sats</span>
                {gpsOk
                  ? <span className="text-xs text-green-400">● FIX</span>
                  : <span className="text-xs text-amber-400">NO FIX</span>
                }
              </div>
            </div>
            {lat !== undefined && lon !== undefined ? (
              <>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Lat</span>
                  <span className="text-slate-200 font-mono">{lat.toFixed(6)}°</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Lon</span>
                  <span className="text-slate-200 font-mono">{lon.toFixed(6)}°</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Alt</span>
                  <span className="text-slate-200 font-mono">{alt?.toFixed(1) ?? '—'} m</span>
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-500 text-center py-1">
                Waiting for GPS fix…
              </div>
            )}
          </div>

          {/* Image count + last update */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">📷</span>
              <span className="text-xs font-bold text-white">{count}</span>
              <span className="text-xs text-slate-500">images</span>
            </div>
            {timeSince !== null && (
              <span className="text-xs text-slate-600">
                {timeSince < 60 ? `${timeSince}s ago` : `${Math.round(timeSince/60)}m ago`}
              </span>
            )}
          </div>

          {/* SD card space (if in offline mode) */}
          {status?.sd_free_mb !== null && status?.sd_free_mb !== undefined && (
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">💾 SD Free</span>
              <span className="text-slate-300">{status.sd_free_mb} MB</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
